import { Link } from "react-router-dom";
import type { GossipEvent } from "@/mocks/events";
import { formatHeat, formatDate, statusColor } from "@/lib/format";

interface EventCardProps {
  event: GossipEvent;
  rank?: number;
  variant?: "default" | "wide" | "compact";
}

export default function EventCard({ event, rank, variant = "default" }: EventCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/events/${event.id}`}
        className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-primary-50/50"
      >
        {rank !== undefined && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-sm font-bold text-foreground-600 group-hover:bg-primary-500 group-hover:text-background-50">
            {rank}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
            {event.title}
          </h3>
          <div className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              {formatHeat(event.heat)}
            </span>
            <span>{event.category}</span>
          </div>
        </div>
        <i className="ri-arrow-right-s-line text-foreground-400 transition-transform group-hover:translate-x-1" />
      </Link>
    );
  }

  const isWide = variant === "wide";

  return (
    <article
      className={`group flex h-full overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-200 hover:-translate-y-1 ${
        isWide ? "flex-col sm:flex-row" : "flex-col"
      }`}
    >
      <Link
        to={`/events/${event.id}`}
        className={`relative block shrink-0 overflow-hidden ${
          isWide ? "h-48 w-full sm:h-auto sm:w-64" : "h-44 w-full"
        }`}
      >
        <img
          src={event.cover}
          alt={`${event.title}事件脉络配图`}
          title={`${event.title} - 网报吃瓜事件库`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium ${statusColor(event.status)}`}
        >
          {event.status}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-800">
            {event.category}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            整理 {event.duration}
          </span>
        </div>

        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/events/${event.id}`} className="transition-colors hover:text-primary-700">
            {event.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{event.summary}</p>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1 font-semibold text-primary-600">
              <i className="ri-fire-line" />
              {formatHeat(event.heat)}
            </span>
            <span>{formatDate(event.updatedAt)} 更新</span>
          </div>
          <Link
            to={`/events/${event.id}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700 whitespace-nowrap"
          >
            看事件串
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </article>
  );
}